/**
 * Custom Defense Resolver
 *
 * Resolves how a single defensive position should move for a given coverage assignment,
 * formation, and route inputs — the per-position analogue of the whole-team,
 * single-`selectedCoverage` logic AnimationEngine runs today.
 *
 * This module is being built incrementally, per the implementation plan:
 *   - Task 3.1 (done): the `PositionRouteInputs` / `PositionMovementResolution` types, and
 *     `resolveManForPosition` — the 'man' coverage extraction.
 *   - Task 4.1 (done): `resolveCover4MatchForPosition` — the 'cover4Match' extraction.
 *   - Task 4.2 (this file, current cut): `resolvePositionMovement` — the public dispatcher —
 *     plus the barrel export from src/rules/index.ts.
 *
 * `resolveManForPosition` reproduces, verbatim, the DEFAULT (no-special-case) reaction lookup
 * `AnimationEngine.executeSimulation` performs today when `selectedCoverage === 'man'` for each
 * of the 7 positions — see the per-position blocks in src/components/AnimationEngine.tsx.
 * "Default" here means: the branch that runs when none of the OTHER built-in coverages' extra
 * special-casing (cover3 / cover3Match / cover4Palms / cover6) applies — man coverage's own
 * "both sides deep → deep_help" logic for FS/SS IS that default behavior (it only ever runs
 * when `selectedCoverage === 'man'`), not an exception to it, so it's included here.
 */

import {
  DefensePositionId,
  CoverageType,
  FormationType,
  Position,
  RouteType,
  RBRouteType,
  Waypoint,
  CoverageReaction,
  DBLBReaction,
  FSReaction,
  CB2Reaction,
  DBLB2Reaction,
  SSReaction,
  LBReaction,
} from '../types';
import {
  getCB1StartPosition,
  getCB2StartPosition,
  getDBLB1StartPosition,
  getDBLB2StartPosition,
  getFSStartPosition,
  getSSStartPosition,
  getLBStartPosition,
  getCoverageReaction,
  getCB2Reaction,
  getDBLB1Reaction,
  getDBLB2Reaction,
  getFSReaction,
  getSSReaction,
  getLBReaction,
  buildObservationFeed,
  resolveManMatch,
  resolveUnderneathMatch,
  resolveHookRead,
  resolveApexMatch,
  resolveDeepZone,
  resolveManTrail,
  resolvePalmsCorner,
  MIN_SIM_HORIZON_MS,
  DEFAULT_MAN_MATCH_PARAMS,
  DEFAULT_MAN_MATCH_PARAMS_RIGHT,
  DEFAULT_MAN_MATCH_PARAMS_FS,
  DEFAULT_MAN_MATCH_PARAMS_SS,
  DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1,
  DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB2,
  DEFAULT_HOOK_READ_PARAMS_LB,
  DEFAULT_MAN_MATCH_PARAMS_CB1_COVER3,
  DEFAULT_MAN_MATCH_PARAMS_CB2_COVER3,
  DEFAULT_APEX_MATCH_PARAMS_DBLB1,
  DEFAULT_APEX_MATCH_PARAMS_SS,
  DEFAULT_HOOK_READ_PARAMS_LB_COVER3,
  DEFAULT_HOOK_READ_PARAMS_DBLB2_COVER3,
  DEFAULT_DEEP_ZONE_PARAMS_FS,
  DEFAULT_MAN_TRAIL_PARAMS,
  DEFAULT_APEX_MATCH_PARAMS_DBLB1_TRIPS,
  DEFAULT_APEX_MATCH_PARAMS_LB_TRIPS,
  DEFAULT_HOOK_READ_PARAMS_DBLB2_TRIPS,
  DEFAULT_HOOK_READ_PARAMS_SS_TRIPS,
  DEFAULT_DEEP_ZONE_PARAMS_SS_COVER6,
  DEFAULT_HOOK_READ_PARAMS_DBLB2_COVER6,
  DEFAULT_PALMS_CORNER_PARAMS_CB1,
  DEFAULT_PALMS_CORNER_PARAMS_CB2,
  DEFAULT_DEEP_ZONE_PARAMS_SS_PALMS,
  DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1_PALMS,
  DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB2_PALMS,
  resolvePalmsSafety,
  DEFAULT_PALMS_SAFETY_PARAMS_FS,
  DEFAULT_PALMS_SAFETY_PARAMS_SS,
  isAssignmentImplemented,
  ReadTraceEntry,
  resolveManMatchDetailed,
  resolveUnderneathMatchDetailed,
  resolveHookReadDetailed,
  resolveManTrailDetailed,
  resolvePalmsCornerDetailed,
  resolvePalmsSafetyDetailed,
} from './index';

/**
 * Route data a single position's resolver needs to reproduce today's per-position 'man'
 * (and, from Task 4.1, 'cover4Match') behavior. All of it is already computed locally in
 * `AnimationEngine.executeSimulation` today — see design.md, "3. resolvePositionMovement",
 * for the full rationale.
 */
export interface PositionRouteInputs {
  /**
   * The RouteType of this position's own keyed receiver — the WR whose route this position's
   * legacy reaction lookup is keyed on today: WR1's route for `cb1`, WR2's for `dblb1`/`fs`,
   * WR3's for `dblb2`/`ss`, WR4's for `cb2`. Unused for `lb`, which keys the RB via `rbRoute`
   * instead of a WR route.
   */
  keyRoute?: RouteType;
  /** This position's own key receiver/RB waypoints, in REAL field coordinates
   *  (already mirrored/offset — same waypoints AnimationEngine feeds its own createXAnimation calls). */
  keyWaypoints: Waypoint[];
  keyRouteDuration: number;
  /** FS/SS 'man' only: the other receiver on the same side, for the deep-help check. */
  partnerRoute?: RouteType;
  partnerWaypoints?: Waypoint[];
  partnerRouteDuration?: number;
  /** LB 'man' only. LB keys the RB's route instead of a WR route. */
  rbRoute?: RBRouteType;
  rbRouteDuration?: number;
  /**
   * The other eligible receivers' waypoints (WRs), in REAL field coordinates. Used by the LB
   * (cover4Match) and both hook defenders (cover3Match) to look for a crosser to match, and by
   * the Cover 3 hook defenders as crosser candidates.
   */
  crosserWaypoints?: Waypoint[][];
  /**
   * The RB's waypoints, in REAL field coordinates. Needed by the Cover 3 Match apex defenders
   * (RB-to-flat read) and the two Cover 3 hook defenders (whose key IS the RB).
   */
  rbWaypoints?: Waypoint[];
}

/**
 * How a single defensive position should move for the play, resolved independently of every
 * other position's assignment.
 */
export type PositionMovementResolution =
  | { mode: 'hold' }
  | {
      mode: 'legacyReaction';
      startPosition: Position;
      reaction: CoverageReaction | DBLBReaction | FSReaction | CB2Reaction | DBLB2Reaction | SSReaction | LBReaction;
      routeDuration: number;
    }
  | {
      mode: 'perceptionTrajectory';
      startPosition: Position;
      trajectory: Position[];
      horizonMs: number;
      /**
       * Tick-by-tick record of the read behind this trajectory — which rule fired, when, and
       * what he had latched. Optional because it is only populated where the resolver calls the
       * `Detailed` variant; consumers must treat it as diagnostic, never as behavior.
       */
      trace?: ReadTraceEntry[];
    };

/**
 * Routes considered "deep" (10+ yards) for the FS/SS man-coverage deep-help check —
 * identical to the `deepRoutes` array in `AnimationEngine.executeSimulation`.
 */
const DEEP_MAN_ROUTES: RouteType[] = ['deepOut', 'deepIn', 'comeback', 'go', 'post', 'corner'];

function isDeepManRoute(route: RouteType | undefined): boolean {
  return !!route && DEEP_MAN_ROUTES.includes(route);
}

/**
 * Resolve how a single position moves under 'man' coverage — the extraction of today's
 * default (no-special-case) per-position 'man' branch in AnimationEngine. Reproduces the exact
 * same start position, reaction lookup, and route duration as the legacy whole-team path, so
 * behavior is unchanged whether 'man' is the whole team's coverage or one position's custom
 * assignment (Requirement 4.2).
 */
export function resolveManForPosition(
  position: DefensePositionId,
  formation: FormationType,
  inputs: PositionRouteInputs
): PositionMovementResolution {
  switch (position) {
    // CB1 keys WR1: today's default is a direct start-position + reaction lookup, no
    // special-casing for 'man' (see loadCoverageData / the top of executeSimulation).
    case 'cb1': {
      const startPosition = getCB1StartPosition('man', formation);
      const reaction = getCoverageReaction('man', inputs.keyRoute ?? '', formation);
      return reaction
        ? { mode: 'legacyReaction', startPosition, reaction, routeDuration: inputs.keyRouteDuration }
        : { mode: 'hold' };
    }

    // CB2 keys WR4: same shape as CB1, mirrored.
    case 'cb2': {
      const startPosition = getCB2StartPosition('man', formation);
      const reaction = getCB2Reaction('man', inputs.keyRoute ?? '', formation);
      return reaction
        ? { mode: 'legacyReaction', startPosition, reaction, routeDuration: inputs.keyRouteDuration }
        : { mode: 'hold' };
    }

    // DB/LB1 keys WR2: default (no special-casing runs for 'man') is a direct lookup.
    case 'dblb1': {
      const startPosition = getDBLB1StartPosition('man', formation);
      const reaction = getDBLB1Reaction('man', inputs.keyRoute ?? '', formation);
      return reaction
        ? { mode: 'legacyReaction', startPosition, reaction, routeDuration: inputs.keyRouteDuration }
        : { mode: 'hold' };
    }

    // DB/LB2 keys WR3: same shape as DB/LB1, mirrored.
    case 'dblb2': {
      const startPosition = getDBLB2StartPosition('man', formation);
      const reaction = getDBLB2Reaction('man', inputs.keyRoute ?? '', formation);
      return reaction
        ? { mode: 'legacyReaction', startPosition, reaction, routeDuration: inputs.keyRouteDuration }
        : { mode: 'hold' };
    }

    // FS keys WR2 and additionally provides deep help on the left side (WR1 + WR2): if
    // both are deep, stay centered ('deep_help'); if only one is deep, match that one
    // (own key or partner); otherwise use the default own-key reaction already implied.
    case 'fs': {
      const startPosition = getFSStartPosition('man', formation);
      const ownDeep = isDeepManRoute(inputs.keyRoute);
      const partnerDeep = isDeepManRoute(inputs.partnerRoute);

      let routeTrigger: string | undefined = inputs.keyRoute;
      let routeDuration = inputs.keyRouteDuration;

      if (ownDeep && partnerDeep) {
        // Multiple deep threats — stay centered in deep half, don't commit to one.
        routeTrigger = 'deep_help';
        routeDuration = Math.max(inputs.keyRouteDuration, inputs.partnerRouteDuration ?? 0);
      } else if (partnerDeep) {
        routeTrigger = inputs.partnerRoute;
        routeDuration = inputs.partnerRouteDuration ?? inputs.keyRouteDuration;
      } else if (ownDeep) {
        routeTrigger = inputs.keyRoute;
        routeDuration = inputs.keyRouteDuration;
      }
      // else: neither deep — default reaction keyed on the own route (moderate drop).

      const reaction = getFSReaction('man', routeTrigger ?? '', formation);
      return reaction
        ? { mode: 'legacyReaction', startPosition, reaction, routeDuration }
        : { mode: 'hold' };
    }

    // SS keys WR3 and provides deep help on the right side (WR3 + WR4) — mirror of FS.
    case 'ss': {
      const startPosition = getSSStartPosition('man', formation);
      const ownDeep = isDeepManRoute(inputs.keyRoute);
      const partnerDeep = isDeepManRoute(inputs.partnerRoute);

      let routeTrigger: string | undefined = inputs.keyRoute;
      let routeDuration = inputs.keyRouteDuration;

      if (ownDeep && partnerDeep) {
        routeTrigger = 'deep_help';
        routeDuration = Math.max(inputs.keyRouteDuration, inputs.partnerRouteDuration ?? 0);
      } else if (partnerDeep) {
        routeTrigger = inputs.partnerRoute;
        routeDuration = inputs.partnerRouteDuration ?? inputs.keyRouteDuration;
      } else if (ownDeep) {
        routeTrigger = inputs.keyRoute;
        routeDuration = inputs.keyRouteDuration;
      }

      const reaction = getSSReaction('man', routeTrigger ?? '', formation);
      return reaction
        ? { mode: 'legacyReaction', startPosition, reaction, routeDuration }
        : { mode: 'hold' };
    }

    // LB keys the RB (not a WR) in man coverage — covers the RB man-to-man on all routes.
    case 'lb': {
      const startPosition = getLBStartPosition('man', formation);
      const reaction = getLBReaction('man', inputs.rbRoute ?? '', formation);
      return reaction
        ? { mode: 'legacyReaction', startPosition, reaction, routeDuration: inputs.rbRouteDuration ?? 0 }
        : { mode: 'hold' };
    }
  }
}

/**
 * Resolve how a single position moves under 'cover4Match' coverage — the extraction of
 * today's perception-engine block in AnimationEngine.executeSimulation (the
 * `if (selectedCoverage === 'cover4Match') { ... }` blocks for CB1, CB2, FS, and SS).
 *
 * CB1/CB2/FS/SS each run a man-match ("MOD") read against their own keyed receiver's
 * observation feed, using that position's own DEFAULT_MAN_MATCH_PARAMS* variant. DB/LB1 and
 * DB/LB2 run the underneath "Wall-Read on #2" read (resolveUnderneathMatch), mirrored across
 * midfield. LB runs the "Play RB" hook read (resolveHookRead) keyed on the RB.
 */
export function resolveCover4MatchForPosition(
  position: DefensePositionId,
  formation: FormationType,
  inputs: PositionRouteInputs
): PositionMovementResolution {
  switch (position) {
    // CB1 keys WR1 — left-side man-match, bails to the deep left third.
    case 'cb1': {
      const startPosition = getCB1StartPosition('cover4Match', formation);
      const horizonMs = Math.max(inputs.keyRouteDuration, MIN_SIM_HORIZON_MS);
      const keyFeed = buildObservationFeed(inputs.keyWaypoints, horizonMs);
      const read = resolveManMatchDetailed(startPosition, keyFeed, DEFAULT_MAN_MATCH_PARAMS);
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }

    // CB2 keys WR4 — mirror of CB1, bails to the deep right third.
    case 'cb2': {
      const startPosition = getCB2StartPosition('cover4Match', formation);
      const horizonMs = Math.max(inputs.keyRouteDuration, MIN_SIM_HORIZON_MS);
      const keyFeed = buildObservationFeed(inputs.keyWaypoints, horizonMs);
      const read = resolveManMatchDetailed(startPosition, keyFeed, DEFAULT_MAN_MATCH_PARAMS_RIGHT);
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }

    // FS keys WR2 — patient, over-the-top read that backpedals straight into the deep
    // inside-left quarter.
    case 'fs': {
      const startPosition = getFSStartPosition('cover4Match', formation);
      const horizonMs = Math.max(inputs.keyRouteDuration, MIN_SIM_HORIZON_MS);
      const keyFeed = buildObservationFeed(inputs.keyWaypoints, horizonMs);
      const read = resolveManMatchDetailed(startPosition, keyFeed, DEFAULT_MAN_MATCH_PARAMS_FS);
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }

    // SS keys WR3 — mirror of FS, backpedals into the deep inside-right quarter (doubles). In
    // trips WR3 is a strong-side (left) receiver, so the right SS has no #2 to key: he plays the
    // weak "poach" deep-help half instead (mirrors Cover 4 Palms trips).
    case 'ss': {
      const startPosition = getSSStartPosition('cover4Match', formation);
      const horizonMs = Math.max(inputs.keyRouteDuration, MIN_SIM_HORIZON_MS);
      if (formation === 'trips') {
        const trajectory = resolveDeepZone(startPosition, horizonMs, DEFAULT_DEEP_ZONE_PARAMS_SS_PALMS);
        return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs };
      }
      const keyFeed = buildObservationFeed(inputs.keyWaypoints, horizonMs);
      const read = resolveManMatchDetailed(startPosition, keyFeed, DEFAULT_MAN_MATCH_PARAMS_SS);
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }

    // DB/LB1 keys WR2 — underneath "Wall-Read on #2": jumps a short out/hitch man-to-man,
    // otherwise (vertical or short-inside) passes #2 off and settles into the left curl-flat.
    // Also takes the RB on a "Push" (fast/wide release to the left flat) and carries him.
    case 'dblb1': {
      const startPosition = getDBLB1StartPosition('cover4Match', formation);
      const horizonMs = Math.max(inputs.keyRouteDuration, MIN_SIM_HORIZON_MS);
      const keyFeed = buildObservationFeed(inputs.keyWaypoints, horizonMs);
      const rbFeed = buildObservationFeed(inputs.rbWaypoints ?? [], horizonMs);
      // #1 (WR1) feed, so the apex can come off #2 to cover a #1 short in-breaker when no one is
      // in the flat ("reroute #2, cover #1"). partnerWaypoints carries WR1 for the left apex.
      const key1Feed = buildObservationFeed(inputs.partnerWaypoints ?? [], horizonMs);
      const read = resolveUnderneathMatchDetailed(startPosition, keyFeed, DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1, rbFeed, key1Feed);
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }

    // DB/LB2: mirror of DB/LB1's underneath "Wall-Read on #2" on WR3, taking the RB on a Push to
    // the right flat (doubles). In trips DB/LB2 is the WEAK apex — mans the RB on any release to
    // the weak (right) side, else matches a strong-side crosser (mirrors Cover 4 Palms trips).
    case 'dblb2': {
      const startPosition = getDBLB2StartPosition('cover4Match', formation);
      const horizonMs = Math.max(inputs.keyRouteDuration, MIN_SIM_HORIZON_MS);
      if (formation === 'trips') {
        const crossers = inputs.crosserWaypoints ?? [];
        const rbFeed = buildObservationFeed(inputs.rbWaypoints ?? [], horizonMs);
        const crosserFeeds = [crossers[0], crossers[1], crossers[2]].map((wps) => buildObservationFeed(wps ?? [], horizonMs));
        const trajectory = resolveHookRead(startPosition, rbFeed, DEFAULT_HOOK_READ_PARAMS_SS_TRIPS, crosserFeeds);
        return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs };
      }
      const keyFeed = buildObservationFeed(inputs.keyWaypoints, horizonMs);
      const rbFeed = buildObservationFeed(inputs.rbWaypoints ?? [], horizonMs);
      // #1 (WR4) feed, so the right apex can come off #2 to cover a #1 short in-breaker when no
      // one is in the flat. partnerWaypoints carries WR4 for the right apex.
      const key1Feed = buildObservationFeed(inputs.partnerWaypoints ?? [], horizonMs);
      const read = resolveUnderneathMatchDetailed(startPosition, keyFeed, DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB2, rbFeed, key1Feed);
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }

    // LB keys the RB (#3) — "Play RB" hook read: mans up on a central checkdown release,
    // otherwise holds the hook (doesn't chase a fast flat/out release or a stay-in).
    // The LB's key waypoints are the RB's route (passed as keyWaypoints/keyRouteDuration).
    case 'lb': {
      const startPosition = getLBStartPosition('cover4Match', formation);
      const horizonMs = Math.max(inputs.keyRouteDuration, MIN_SIM_HORIZON_MS);
      const keyFeed = buildObservationFeed(inputs.keyWaypoints, horizonMs);
      // The WRs are crosser candidates the LB matches when there's no RB to cover.
      const crosserFeeds = (inputs.crosserWaypoints ?? []).map((wps) =>
        buildObservationFeed(wps, horizonMs)
      );
      const read = resolveHookReadDetailed(startPosition, keyFeed, DEFAULT_HOOK_READ_PARAMS_LB, crosserFeeds);
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }
  }
}

/**
 * Resolve how a single position moves under 'cover3Match' coverage (doubles), mirroring the
 * whole-team perception blocks in AnimationEngine. CB1/CB2 run the deep-third MOD man-match on
 * their #1; DB/LB1 and SS run the apex "match #2" read (keying #2, with the same-side #1 and the
 * RB as secondary reads); FS drops the single-high deep middle; LB and DB/LB2 are the two inside
 * hooks (side-aware "Play RB + crossers"). Every read runs against real-field observation feeds —
 * no route names. The feeds share one horizon so they line up index-for-index.
 */
export function resolveCover3MatchForPosition(
  position: DefensePositionId,
  formation: FormationType,
  inputs: PositionRouteInputs
): PositionMovementResolution {
  const horizonMs = Math.max(inputs.keyRouteDuration, MIN_SIM_HORIZON_MS);
  const feed = (wps: Waypoint[] | undefined): ReturnType<typeof buildObservationFeed> =>
    buildObservationFeed(wps ?? [], horizonMs);
  const isTrips = formation === 'trips';
  // Convention: crosserWaypoints = [WR1, WR2, WR3, WR4] in real field coords (see the resolver
  // call sites). Trips reads pick specific receivers from it (WR3 as the apex flat threat, WR1 as
  // the SS #1). Empty when a caller doesn't supply them.
  const crossers = inputs.crosserWaypoints ?? [];

  switch (position) {
    // CB1 keys WR1 — deep left third MOD read (same in trips: WR1 is the strong-side #1).
    case 'cb1': {
      const startPosition = getCB1StartPosition('cover3Match', formation);
      const read = resolveManMatchDetailed(startPosition, feed(inputs.keyWaypoints), DEFAULT_MAN_MATCH_PARAMS_CB1_COVER3);
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }

    // CB2 keys WR4 — deep right third MOD read (doubles); straight man on the single WR4 (trips).
    case 'cb2': {
      const startPosition = getCB2StartPosition('cover3Match', formation);
      const trajectory = isTrips
        ? resolveManTrail(startPosition, feed(inputs.keyWaypoints), DEFAULT_MAN_TRAIL_PARAMS)
        : resolveManMatch(startPosition, feed(inputs.keyWaypoints), DEFAULT_MAN_MATCH_PARAMS_CB2_COVER3);
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs };
    }

    // DB/LB1 apex — match #2 (WR2), reading WR1 (partner). Flat threat is the RB (doubles) or the
    // strong-side #3/WR3 (trips).
    case 'dblb1': {
      const startPosition = getDBLB1StartPosition('cover3Match', formation);
      const trajectory = isTrips
        ? resolveApexMatch(startPosition, feed(inputs.keyWaypoints), DEFAULT_APEX_MATCH_PARAMS_DBLB1_TRIPS, feed(inputs.partnerWaypoints), feed(crossers[2]))
        : resolveApexMatch(startPosition, feed(inputs.keyWaypoints), DEFAULT_APEX_MATCH_PARAMS_DBLB1, feed(inputs.partnerWaypoints), feed(inputs.rbWaypoints));
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs };
    }

    // SS — apex match #2 (WR3) in doubles; the WEAK apex (keys the RB, mans any release to his
    // side, else strong-side crossers) in trips.
    case 'ss': {
      const startPosition = getSSStartPosition('cover3Match', formation);
      const trajectory = isTrips
        ? resolveHookRead(startPosition, feed(inputs.rbWaypoints), DEFAULT_HOOK_READ_PARAMS_SS_TRIPS, crossers.map((wps) => feed(wps)))
        : resolveApexMatch(startPosition, feed(inputs.keyWaypoints), DEFAULT_APEX_MATCH_PARAMS_SS, feed(inputs.partnerWaypoints), feed(inputs.rbWaypoints));
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs };
    }

    // FS — single-high deep middle third (pure zone drop, no key). Same in both formations.
    case 'fs': {
      const startPosition = getFSStartPosition('cover3Match', formation);
      const trajectory = resolveDeepZone(startPosition, horizonMs, DEFAULT_DEEP_ZONE_PARAMS_FS);
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs };
    }

    // LB — left inside hook (keys the RB + crossers) in doubles; MANS the strong-side #3/WR3 in
    // trips (rats to WR2 on a WR3 short out, passes off a WR3 in-breaker).
    case 'lb': {
      const startPosition = getLBStartPosition('cover3Match', formation);
      const trajectory = isTrips
        ? resolveApexMatch(startPosition, feed(crossers[2]), DEFAULT_APEX_MATCH_PARAMS_LB_TRIPS, [], [], feed(crossers[1]))
        : resolveHookRead(
            startPosition,
            feed(inputs.keyWaypoints),
            DEFAULT_HOOK_READ_PARAMS_LB_COVER3,
            crossers.map((wps) => feed(wps)),
            // #2 on his side (WR2), so he can make the "Push" call — take #2 when the back gets
            // out where he cannot reach him. See .kiro/steering/cover3Match.md.
            feed(crossers[1])
          );
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs };
    }

    // DB/LB2 — keys the RB (rbWaypoints, NOT its cover4 #2 key). Right inside hook (doubles) or the
    // middle hook that matches the WR3 crosser with priority, else covers a middle-releasing RB (trips).
    case 'dblb2': {
      const startPosition = getDBLB2StartPosition('cover3Match', formation);
      const trajectory = isTrips
        ? resolveHookRead(startPosition, feed(inputs.rbWaypoints), DEFAULT_HOOK_READ_PARAMS_DBLB2_TRIPS, [feed(crossers[2])])
        : resolveHookRead(
            startPosition,
            feed(inputs.rbWaypoints),
            DEFAULT_HOOK_READ_PARAMS_DBLB2_COVER3,
            crossers.map((wps) => feed(wps)),
            feed(crossers[2]) // #2 on his side (WR3) — the "Push" candidate
          );
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs };
    }
  }
}

/**
 * Resolve how a single position moves under 'cover6' coverage — Cover 4 on the LEFT, Cover 2 on
 * the RIGHT, mirroring the whole-team perception blocks in AnimationEngine.
 *   Left (Cover 4): CB1 MOD man-match #1 (WR1), DB/LB1 wall #2 (WR2), FS robber (reads WR2).
 *   Right (Cover 2): CB2 man #1 (WR4), DB/LB2 match #2 (WR3) [doubles] / single-side crosser
 *     [trips], SS deep HALF.
 *   Middle: LB hook — plays the RB (#3) or a Cover-4-side crosser.
 * All reads run against real-field observation feeds. crosserWaypoints = [WR1..WR4].
 */
export function resolveCover6ForPosition(
  position: DefensePositionId,
  formation: FormationType,
  inputs: PositionRouteInputs
): PositionMovementResolution {
  const horizonMs = Math.max(inputs.keyRouteDuration, MIN_SIM_HORIZON_MS);
  const feed = (wps: Waypoint[] | undefined): ReturnType<typeof buildObservationFeed> =>
    buildObservationFeed(wps ?? [], horizonMs);
  const isTrips = formation === 'trips';
  const crossers = inputs.crosserWaypoints ?? []; // [WR1, WR2, WR3, WR4]

  switch (position) {
    // Cover 4 (left) corner — MOD man-match on WR1 (deep left quarter). Same in trips (Palms).
    case 'cb1': {
      const startPosition = getCB1StartPosition('cover6', formation);
      const read = resolveManMatchDetailed(startPosition, feed(inputs.keyWaypoints));
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }

    // Cover 4 (left) apex — walls WR2 (#2). Doubles jumps a #2 short out (standard Cover 4 Match);
    // trips (3-WR Palms side) passes it off to the Palms corner on the "Out" call.
    case 'dblb1': {
      const startPosition = getDBLB1StartPosition('cover6', formation);
      const read = resolveUnderneathMatchDetailed(
        startPosition,
        feed(inputs.keyWaypoints),
        isTrips ? DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1_PALMS : DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1
      );
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }

    // Cover 4 (left) robber safety — reads WR2 (man vertical, else deep left quarter).
    case 'fs': {
      const startPosition = getFSStartPosition('cover6', formation);
      const read = resolveManMatchDetailed(startPosition, feed(inputs.keyWaypoints), DEFAULT_MAN_MATCH_PARAMS_FS);
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }

    // Cover 2 (right) corner — matches #1 (WR4) man-to-man. Same in trips (single-side man).
    case 'cb2': {
      const startPosition = getCB2StartPosition('cover6', formation);
      const read = resolveManTrailDetailed(startPosition, feed(inputs.keyWaypoints), DEFAULT_MAN_TRAIL_PARAMS);
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }

    // Cover 2 (right) deep-HALF safety (pure deep zone). Same in trips (single-side deep half).
    case 'ss': {
      const startPosition = getSSStartPosition('cover6', formation);
      const trajectory = resolveDeepZone(startPosition, horizonMs, DEFAULT_DEEP_ZONE_PARAMS_SS_COVER6);
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs };
    }

    // Middle hook — plays the RB (#3) + Cover-4-side (left) crossers.
    case 'lb': {
      const startPosition = getLBStartPosition('cover6', formation);
      const crosserFeeds = [feed(crossers[0]), feed(crossers[1])]; // WR1, WR2 (Cover 4 side)
      const read = resolveHookReadDetailed(startPosition, feed(inputs.keyWaypoints), DEFAULT_HOOK_READ_PARAMS_LB, crosserFeeds);
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }

    // Cover 2 (right) apex match #2 = WR3 [doubles]; single (right) side crosser matcher [trips].
    case 'dblb2': {
      const startPosition = getDBLB2StartPosition('cover6', formation);
      const trajectory = isTrips
        ? resolveHookRead(startPosition, feed(inputs.rbWaypoints), DEFAULT_HOOK_READ_PARAMS_DBLB2_COVER6, [feed(crossers[0]), feed(crossers[1]), feed(crossers[2])])
        : resolveUnderneathMatch(startPosition, feed(inputs.keyWaypoints), DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB2);
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs };
    }
  }
}

/**
 * Resolve how a single position moves under 'cover4Palms' coverage — Cover 4 Match with a Palms
 * twist on the corners, mirroring the whole-team perception blocks in AnimationEngine.
 *   Doubles: CB1/CB2 play Palms (MOD man-match on #1, jumping #2's early "Out"); DB/LB1/DB/LB2
 *     wall #2; FS/SS are robber safeties (reusing the man-match FS/SS robber read); LB hammers #3.
 *   Trips (3-WR strong LEFT, single WR4 weak RIGHT): CB1 Palms, DB/LB1 wall #2, FS robber, LB
 *     hammer mirror the strong side; CB2 mans WR4, SS poaches (deep-help zone), DB/LB2 is the weak
 *     apex (mans the RB to his side, else strong-side crossers).
 * All reads run against real-field observation feeds. crosserWaypoints = [WR1, WR2, WR3, WR4].
 *
 * SIMPLIFICATIONS (documented deviations from cover4Palms.md): the robber safety reads #2 only
 * (no "#2 out early → man #1" switch); the trips SS poach is a deep-help zone, not a true bracket;
 * the corner's "Under"/"Smash" calls collapse into the MOD zone drop of the reused man-match read.
 */
export function resolveCover4PalmsForPosition(
  position: DefensePositionId,
  formation: FormationType,
  inputs: PositionRouteInputs
): PositionMovementResolution {
  const horizonMs = Math.max(inputs.keyRouteDuration, MIN_SIM_HORIZON_MS);
  const feed = (wps: Waypoint[] | undefined): ReturnType<typeof buildObservationFeed> =>
    buildObservationFeed(wps ?? [], horizonMs);
  const isTrips = formation === 'trips';
  const crossers = inputs.crosserWaypoints ?? []; // [WR1, WR2, WR3, WR4]

  switch (position) {
    // CB1 plays Palms on WR1 (#1), reading WR2 (#2 = crossers[1]) for the early "Out" call.
    case 'cb1': {
      const startPosition = getCB1StartPosition('cover4Palms', formation);
      const read = resolvePalmsCornerDetailed(startPosition, feed(inputs.keyWaypoints), feed(crossers[1]), DEFAULT_PALMS_CORNER_PARAMS_CB1);
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }

    // DB/LB1 walls #2 (WR2) — like Cover 4 Match, but a #2 short out is passed off to the Palms
    // corner (the "Out" call) rather than jumped. Reads WR1 (#1 = crossers[0]) so it can come off
    // a passed-off #2 to cover a #1 short in-breaker with no one in the flat. Same in trips.
    case 'dblb1': {
      const startPosition = getDBLB1StartPosition('cover4Palms', formation);
      const read = resolveUnderneathMatchDetailed(startPosition, feed(inputs.keyWaypoints), DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB1_PALMS, feed(inputs.rbWaypoints), feed(crossers[0]));
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }

    // FS is the (strong-side) robber safety — reads WR2 (key), WR1 (partner). Mans WR1 over the
    // top if WR2 runs an early short out (corner's "Out" call), else robber read on WR2.
    case 'fs': {
      const startPosition = getFSStartPosition('cover4Palms', formation);
      const read = resolvePalmsSafetyDetailed(startPosition, feed(inputs.keyWaypoints), feed(inputs.partnerWaypoints), DEFAULT_PALMS_SAFETY_PARAMS_FS);
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }

    // CB2: Palms on WR4 (reading WR3 = crossers[2]) in doubles; straight man on the single WR4 in trips.
    case 'cb2': {
      const startPosition = getCB2StartPosition('cover4Palms', formation);
      const trajectory = isTrips
        ? resolveManTrail(startPosition, feed(inputs.keyWaypoints), DEFAULT_MAN_TRAIL_PARAMS)
        : resolvePalmsCorner(startPosition, feed(inputs.keyWaypoints), feed(crossers[2]), DEFAULT_PALMS_CORNER_PARAMS_CB2);
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs };
    }

    // DB/LB2: walls #2 (WR3) in doubles; the weak apex (RB to his side, else strong-side crossers) in trips.
    // In doubles it reads WR4 (#1 = crossers[3]) for the "#1 short in-breaker" come-off.
    case 'dblb2': {
      const startPosition = getDBLB2StartPosition('cover4Palms', formation);
      const trajectory = isTrips
        ? resolveHookRead(startPosition, feed(inputs.rbWaypoints), DEFAULT_HOOK_READ_PARAMS_SS_TRIPS, [feed(crossers[0]), feed(crossers[1]), feed(crossers[2])])
        : resolveUnderneathMatch(startPosition, feed(inputs.keyWaypoints), DEFAULT_UNDERNEATH_MATCH_PARAMS_DBLB2_PALMS, feed(inputs.rbWaypoints), feed(crossers[3]));
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs };
    }

    // SS: robber safety on WR3 (reading WR4) in doubles — mans WR4 over the top on the corner's
    // "Out" call, else robber read on WR3; the weak "poach" safety (deep-help zone) in trips.
    case 'ss': {
      const startPosition = getSSStartPosition('cover4Palms', formation);
      const trajectory = isTrips
        ? resolveDeepZone(startPosition, horizonMs, DEFAULT_DEEP_ZONE_PARAMS_SS_PALMS)
        : resolvePalmsSafety(startPosition, feed(inputs.keyWaypoints), feed(inputs.partnerWaypoints), DEFAULT_PALMS_SAFETY_PARAMS_SS);
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs };
    }

    // LB hammers #3 (RB) — "Play RB + crossers" hook read. All WRs in doubles; strong (left) WR1/WR2 in trips.
    case 'lb': {
      const startPosition = getLBStartPosition('cover4Palms', formation);
      const crosserSources = isTrips ? [crossers[0], crossers[1]] : crossers;
      const crosserFeeds = crosserSources.map((wps) => feed(wps));
      const read = resolveHookReadDetailed(startPosition, feed(inputs.keyWaypoints), DEFAULT_HOOK_READ_PARAMS_LB, crosserFeeds);
      const trajectory = read.trajectory;
      return { mode: 'perceptionTrajectory', startPosition, trajectory, horizonMs, trace: read.trace };
    }
  }
}

/**
 * Resolve how a single position moves for a given per-position coverage assignment — the
 * public dispatcher every consumer (AnimationEngine, PlayArt, and their property tests) calls.
 *
 * Positions never resolve to real movement for an assignment the registry hasn't built
 * behavior for yet (Requirement 4.3) — `isAssignmentImplemented` is the single source of
 * truth for that, shared with the Custom Defense Builder's completeness classification, so a
 * registry update is reflected here automatically without any change to this dispatcher.
 */
export function resolvePositionMovement(
  position: DefensePositionId,
  coverage: CoverageType,
  formation: FormationType,
  inputs: PositionRouteInputs
): PositionMovementResolution {
  if (!isAssignmentImplemented(position, coverage)) {
    return { mode: 'hold' };
  }
  if (coverage === 'man') {
    return resolveManForPosition(position, formation, inputs);
  }
  if (coverage === 'cover4Match') {
    return resolveCover4MatchForPosition(position, formation, inputs);
  }
  if (coverage === 'cover3Match') {
    return resolveCover3MatchForPosition(position, formation, inputs);
  }
  if (coverage === 'cover6') {
    return resolveCover6ForPosition(position, formation, inputs);
  }
  if (coverage === 'cover4Palms') {
    return resolveCover4PalmsForPosition(position, formation, inputs);
  }
  return { mode: 'hold' };
}
